import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import { getTimeDiff } from '../../../utils/date-helpers';

interface GetEventPlacementValuesParams {
  calendarDate: string; // format: YYYY-MM-DD
  startDate: string; // format: MM/DD/YYYY
  startHour: string; // format: hh:mm A
  endHour: string; // format: hh:mm A
  pixelsPerMinute: number;
}

interface EventPlacementValues {
  totalDuration: number; // in minutes
  startGridColumn: number; // in pixels
  endGridColumn: number; // in pixels
  isYesterdayEvent: boolean; // true if the event starts before the calendar date
  isOverlappingEvent: boolean; // true if the event overlaps into the next day
}

const EVENT_CARD_OFFSET_IN_MINUTES = 15;
const OVERLAPPING_INDICATION_OFFSET_IN_MINUTES = 5;

export const getEventPlacementValues = ({
  calendarDate,
  startDate,
  startHour,
  endHour,
  pixelsPerMinute,
}: GetEventPlacementValuesParams): EventPlacementValues => {
  const calendarStartDateTime = dayjs(calendarDate, 'YYYY-MM-DD').startOf('day');
  const eventStartDateTime = dayjs(`${startDate} ${startHour}`, 'MM/DD/YYYY hh:mm A');

  const isYesterdayEvent = eventStartDateTime.isBefore(calendarStartDateTime, 'day');
  let durationInMinutes = getTimeDiff(startHour, endHour, { format: 'hh:mm A' });

  let modifiedEndHour = endHour;
  // If the event the duration is less than the offset, adjust the end hour
  if (durationInMinutes > 0 && durationInMinutes < EVENT_CARD_OFFSET_IN_MINUTES) {
    modifiedEndHour = dayjs(eventStartDateTime).add(EVENT_CARD_OFFSET_IN_MINUTES, 'minute').format('hh:mm A');
    durationInMinutes = getTimeDiff(startHour, modifiedEndHour, { format: 'hh:mm A' });
  }

  const isExactlyMidnight = endHour === '12:00 AM';

  const isOverlappingEvent = isExactlyMidnight ? false : durationInMinutes <= 0;
  const totalStartMinutes = isYesterdayEvent
    ? -OVERLAPPING_INDICATION_OFFSET_IN_MINUTES
    : eventStartDateTime.diff(calendarStartDateTime, 'minute');
  const totalEndMinutes = isYesterdayEvent
    ? dayjs(`${calendarDate} ${modifiedEndHour}`).diff(calendarStartDateTime, 'minute')
    : isOverlappingEvent
    ? 24 * 60 + OVERLAPPING_INDICATION_OFFSET_IN_MINUTES
    : isExactlyMidnight
    ? 24 * 60
    : totalStartMinutes + durationInMinutes;

  return {
    isYesterdayEvent,
    isOverlappingEvent,
    totalDuration: totalEndMinutes - totalStartMinutes,
    startGridColumn: totalStartMinutes * pixelsPerMinute,
    endGridColumn: totalEndMinutes * pixelsPerMinute,
  };
};

export const getNonAttendedEventStatusIconName = (eventStatus?: EventStatus) => {
  switch (eventStatus) {
    case EventStatus.CANCELED:
      return 'block';
    case EventStatus.NO_SHOW:
      return 'user-x';
    default:
      return '';
  }
};

// Create a unique column ID for appointment drag-and-drop operations
export const createColumnId = (locationId = '', entityId = '') => {
  return `${locationId}_${entityId}`;
};

export const parseColumnId = (columnId = '') => {
  const [locationId, entityId] = columnId.split('_');
  return { locationId, entityId };
};
